/**
 * Which search term "Find the order" prefills for an email that has no
 * linked order (kanban 0c4b9133, usage-review change 1).
 *
 * Precedence, in the card's words: the customer number when the email is
 * mapped, otherwise the sender's company name. An unmapped email carries no
 * company field, so the company is taken from the best signal available:
 * the name Merlin proposed for a new customer, then the sender's domain stem
 * (never a public mailbox domain), then the sender's display name.
 *
 * The Orders list search is a substring match over customer name, customer
 * number, Sage order number, PO number and salesperson name, so any of these
 * is a workable first query; the person can edit it in place.
 */

/**
 * Does the list row say an order record is linked to this email? The inbox
 * sets has_order only while a live OrderRecord exists (PR #950) and
 * order_record_status carries that record's status. One rule, read by the
 * modal wrapper (open beside the order) and the panel (which header action).
 */
export function emailHasLinkedOrder(
  email: { hasOrder?: boolean | null; orderRecordStatus?: string | null } | null | undefined,
): boolean {
  return !!(email?.hasOrder || email?.orderRecordStatus);
}

export type EmailOrderSearchSource =
  | 'customer_no'
  | 'proposed_customer'
  | 'sender_domain'
  | 'sender_name';

export interface EmailOrderSearch {
  /** The prefilled search term; empty when nothing usable is known. */
  term: string;
  /** The mapped customer number, or null when the email is unmapped. */
  customerNo: string | null;
  source: EmailOrderSearchSource | null;
}

/** Mailbox providers whose domain says nothing about the company. */
const PUBLIC_MAILBOX_DOMAINS = new Set([
  'gmail.com',
  'googlemail.com',
  'yahoo.com',
  'ymail.com',
  'outlook.com',
  'hotmail.com',
  'live.com',
  'msn.com',
  'aol.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'protonmail.com',
  'proton.me',
  'comcast.net',
  'att.net',
  'verizon.net',
  'sbcglobal.net',
]);

/** Minimal view of the mapped email the helper reads. */
export interface EmailOrderSearchInput {
  email?: string | null;
  from?: string | null;
  customer?: {
    customerNo?: string | null;
    customer_no?: string | null;
    domain?: string | null;
  } | null;
  proposals?: {
    proposed_new_customer?: { name?: string | null } | null;
  } | null;
}

export interface CustomerOverrideInput {
  customerNo?: string | null;
}

function clean(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * The company-ish stem of a sender domain: "orders@greatlakesveneer.com"
 * gives "greatlakesveneer"; a public mailbox domain gives "".
 */
export function senderDomainStem(address: string | null | undefined): string {
  const at = clean(address).toLowerCase().split('@');
  if (at.length !== 2) return '';
  const domain = at[1];
  if (!domain || PUBLIC_MAILBOX_DOMAINS.has(domain)) return '';
  const labels = domain.split('.').filter(Boolean);
  if (labels.length < 2) return '';
  // Drop the public suffix; for "co.uk"-style suffixes drop two labels.
  const secondLevel = labels[labels.length - 2];
  const stem =
    labels.length >= 3 && secondLevel.length <= 3
      ? labels[labels.length - 3]
      : secondLevel;
  return stem && stem.length >= 3 ? stem : '';
}

export function resolveEmailOrderSearch(
  email: EmailOrderSearchInput | null | undefined,
  customerOverride?: CustomerOverrideInput | null,
): EmailOrderSearch {
  const customerNo =
    clean(customerOverride?.customerNo)
    || clean(email?.customer?.customerNo)
    || clean(email?.customer?.customer_no);
  if (customerNo) {
    return { term: customerNo, customerNo, source: 'customer_no' };
  }

  const proposed = clean(email?.proposals?.proposed_new_customer?.name);
  if (proposed) {
    return { term: proposed, customerNo: null, source: 'proposed_customer' };
  }

  const stem = senderDomainStem(email?.email);
  if (stem) {
    return { term: stem, customerNo: null, source: 'sender_domain' };
  }

  const name = clean(email?.from);
  if (name) {
    return { term: name, customerNo: null, source: 'sender_name' };
  }

  return { term: '', customerNo: null, source: null };
}
